import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  LinearProgress,
  Alert,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  TextField,
  Chip,
  Divider,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import WarningIcon from "@mui/icons-material/Warning";
import dayjs, { Dayjs } from "dayjs";
import { addBulkAttendanceExcel } from "../../Service/Apis";

interface ImportResponse {
  records_created: number;
  records_updated: number;
  records_skipped: number;
  total_rows: number;
  errors: string[];
  warnings: string[];
  message: string;
  success: boolean;
}

interface AttendanceImportDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const AttendanceImportDialog: React.FC<AttendanceImportDialogProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [response, setResponse] = useState<ImportResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      const selectedFile = event.target.files[0];
      validateAndSetFile(selectedFile);
    }
  };

  const validateAndSetFile = (selectedFile: File) => {
    // Validate file type
    if (!selectedFile.name.match(/\.(csv|xlsx|xls)$/i)) {
      setError("Please select a valid CSV or Excel file (.csv, .xlsx, .xls)");
      return;
    }

    // Validate file size (10MB limit)
    if (selectedFile.size > 10 * 1024 * 1024) {
      setError("File size must be less than 10MB");
      return;
    }

    setFile(selectedFile);
    setError(null);
    setResponse(null);
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragOver(false);
    
    if (event.dataTransfer.files && event.dataTransfer.files[0]) {
      validateAndSetFile(event.dataTransfer.files[0]);
    }
  };

  const handleDownloadTemplate = () => {
    // Create a comprehensive CSV template with all required columns
    const template = [
      [
        "Employee Number",
        "Employee Name", 
        "Date",
        "Shift Policy",
        "First In",
        "Last Out",
        "Attendance Remarks",
        "In Location",
        "Out Location",
        "Late In/ Early Out",
        "Hours Worked",
        "Overtime",
        "Deficit Hours",
        "Status",
        "Status Remark",
        "Regularised",
        "Additional Information",
      ],
      [
        "EMP001",
        "John Doe",
        "2024-04-01",
        "Standard Shift",
        "09:00",
        "17:30",
        "On time arrival",
        "Office - Main Building",
        "Office - Main Building",
        "No",
        "8.5",
        "0.5",
        "0",
        "Present",
        "Regular working day",
        "No",
        "Completed all assigned tasks",
      ],
      [
        "EMP002",
        "Jane Smith",
        "2024-04-01",
        "Flexible Shift",
        "10:00",
        "18:30",
        "Late arrival due to traffic",
        "Office - Annex Building",
        "Office - Annex Building",
        "Yes",
        "8.5",
        "0",
        "0",
        "Present",
        "Late arrival regularized",
        "Yes",
        "Attended client meeting",
      ],
      [
        "EMP003",
        "Mike Johnson",
        "2024-04-01",
        "Remote Work",
        "",
        "",
        "Working from home",
        "Home",
        "Home",
        "No",
        "8.0",
        "0",
        "0",
        "Work From Home",
        "Approved WFH request",
        "No",
        "Completed project deliverables",
      ],
      [
        "EMP004",
        "Sarah Wilson",
        "2024-04-01",
        "Standard Shift",
        "",
        "",
        "On approved leave",
        "",
        "",
        "No",
        "0",
        "0",
        "8",
        "Leave - Paid Leave",
        "Annual leave",
        "No",
        "Vacation leave approved by manager",
      ],
    ];

    const csv = template.map((row) => row.join(",")).join("\n");
    const element = document.createElement("a");
    element.setAttribute(
      "href",
      "data:text/csv;charset=utf-8," + encodeURIComponent(csv)
    );
    element.setAttribute("download", "attendance_import_template.csv");
    element.style.display = "none";
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  const handleImport = async () => {
    if (!file) {
      setError("Please select a file to import");
      return;
    }

    const formData = new FormData();
    formData.append("file", file);

    setLoading(true);
    setError(null);
    setUploadProgress(0);

    try {
      const token = localStorage.getItem("token");
      
      // Simulate progress for better UX
      const progressInterval = setInterval(() => {
        setUploadProgress((prev) => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return prev;
          }
          return prev + 10;
        });
      }, 200);

      const response = await fetch(addBulkAttendanceExcel, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      clearInterval(progressInterval);
      setUploadProgress(100);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || 
          errorData.error || 
          `Import failed with status ${response.status}. Please check your file format and try again.`
        );
      }

      const data: ImportResponse = await response.json();
      setResponse(data);

      // Auto-refresh data after successful import
      if (data.success && (data.records_created > 0 || data.records_updated > 0)) {
        setTimeout(() => {
          onSuccess();
        }, 1500);
      }
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "An unexpected error occurred during import. Please try again."
      );
    } finally {
      setLoading(false);
      setUploadProgress(0);
    }
  };

  const handleClose = () => {
    if (loading) return; // Prevent closing during upload
    
    setFile(null);
    setResponse(null);
    setError(null);
    setUploadProgress(0);
    onClose();
  };

  const getStatusIcon = (type: 'success' | 'error' | 'warning') => {
    switch (type) {
      case 'success':
        return <CheckCircleIcon sx={{ color: 'success.main', mr: 1 }} />;
      case 'error':
        return <ErrorIcon sx={{ color: 'error.main', mr: 1 }} />;
      case 'warning':
        return <WarningIcon sx={{ color: 'warning.main', mr: 1 }} />;
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="md" 
      fullWidth
      disableEscapeKeyDown={loading}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CloudUploadIcon color="primary" />
          <Typography variant="h6">Import Attendance Records</Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          {/* Success Message */}
          {response && response.success && (
            <Alert 
              severity="success" 
              sx={{ mb: 2 }}
              icon={getStatusIcon('success')}
            >
              <Typography variant="body2" fontWeight="bold">
                ✓ Import Completed Successfully!
              </Typography>
              <Box sx={{ mt: 1 }}>
                <Chip 
                  label={`${response.records_created} Created`} 
                  color="success" 
                  size="small" 
                  sx={{ mr: 1, mb: 0.5 }} 
                />
                {response.records_updated > 0 && (
                  <Chip 
                    label={`${response.records_updated} Updated`} 
                    color="info" 
                    size="small" 
                    sx={{ mr: 1, mb: 0.5 }} 
                  />
                )}
                {response.records_skipped > 0 && (
                  <Chip 
                    label={`${response.records_skipped} Skipped`} 
                    color="warning" 
                    size="small" 
                    sx={{ mr: 1, mb: 0.5 }} 
                  />
                )}
                <Chip 
                  label={`${response.total_rows} Total Rows`} 
                  color="default" 
                  size="small" 
                  sx={{ mb: 0.5 }} 
                />
              </Box>
              <Typography variant="body2" sx={{ mt: 1 }}>
                {response.message}
              </Typography>
            </Alert>
          )}

          {/* Error Message */}
          {(error || (response && !response.success)) && (
            <Alert 
              severity="error" 
              sx={{ mb: 2 }}
              icon={getStatusIcon('error')}
            >
              <Typography variant="body2" fontWeight="bold">
                Import Failed
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                {error || response?.message || "An unknown error occurred"}
              </Typography>
            </Alert>
          )}

          {/* Info Text */}
          {!response && !loading && (
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2">
                <strong>Upload Requirements:</strong>
              </Typography>
              <Typography variant="body2" component="div" sx={{ mt: 1 }}>
                • CSV or Excel file (.csv, .xlsx, .xls) with attendance data
                <br />
                • Employee Number must match existing employee codes
                <br />
                • Date format: YYYY-MM-DD (e.g., 2024-04-01)
                <br />
                • Time format: HH:MM (e.g., 09:00, 17:30)
                <br />
                • Regularised field: "Yes", "No", "TRUE", "FALSE", or boolean
                <br />
                • Maximum file size: 10MB
              </Typography>
            </Alert>
          )}

          {/* File Upload Area */}
          {!response && (
            <>
              <Box
                sx={{
                  border: dragOver ? "2px dashed #1976d2" : "2px dashed #ccc",
                  borderRadius: 2,
                  p: 4,
                  textAlign: "center",
                  mb: 3,
                  cursor: loading ? "not-allowed" : "pointer",
                  backgroundColor: dragOver ? "#f3f8ff" : "#fafafa",
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    backgroundColor: loading ? "#fafafa" : "#f0f0f0",
                    borderColor: loading ? "#ccc" : "#999",
                  },
                }}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                <input
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  onChange={handleFileChange}
                  style={{ display: "none" }}
                  id="file-input"
                  disabled={loading}
                />
                <label
                  htmlFor="file-input"
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    cursor: loading ? "not-allowed" : "pointer",
                  }}
                >
                  <CloudUploadIcon
                    sx={{ 
                      fontSize: 48, 
                      color: dragOver ? "#1976d2" : "#666", 
                      mb: 2,
                      transition: "color 0.2s ease-in-out"
                    }}
                  />
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    {file ? file.name : "Drop your file here or click to browse"}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Supported formats: CSV, XLSX, XLS (Max 10 MB)
                  </Typography>
                  {file && (
                    <Box sx={{ mt: 2 }}>
                      <Chip
                        label={`${(file.size / 1024 / 1024).toFixed(2)} MB`}
                        color="primary"
                        size="small"
                        variant="outlined"
                      />
                    </Box>
                  )}
                </label>
              </Box>

              {/* Progress Bar */}
              {loading && (
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}>
                    <CircularProgress size={20} />
                    <Typography variant="body2">
                      Processing file... {uploadProgress}%
                    </Typography>
                  </Box>
                  <LinearProgress 
                    variant="determinate" 
                    value={uploadProgress} 
                    sx={{ height: 8, borderRadius: 4 }}
                  />
                </Box>
              )}

              {/* Download Template Button */}
              <Button
                startIcon={<FileDownloadIcon />}
                onClick={handleDownloadTemplate}
                fullWidth
                variant="outlined"
                size="large"
                disabled={loading}
                sx={{ mb: 2 }}
              >
                Download Sample Template
              </Button>
            </>
          )}

          {/* Warnings */}
          {response && response.warnings && response.warnings.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  {getStatusIcon('warning')}
                  Warnings ({response.warnings.length}):
                </Typography>
                <Box
                  sx={{
                    maxHeight: 150,
                    overflowY: "auto",
                    mt: 1,
                  }}
                >
                  <List dense>
                    {response.warnings.map((warning, index) => (
                      <ListItem key={index} disableGutters>
                        <ListItemText
                          primary={warning}
                          primaryTypographyProps={{
                            variant: "body2",
                            color: "warning.dark",
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              </Alert>
            </Box>
          )}

          {/* Error Details */}
          {response && response.errors && response.errors.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Alert severity="error" sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  {getStatusIcon('error')}
                  Errors ({response.errors.length}):
                </Typography>
                <Box
                  sx={{
                    maxHeight: 200,
                    overflowY: "auto",
                    mt: 1,
                  }}
                >
                  <List dense>
                    {response.errors.map((error, index) => (
                      <ListItem key={index} disableGutters>
                        <ListItemText
                          primary={error}
                          primaryTypographyProps={{
                            variant: "body2",
                            color: "error.dark",
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              </Alert>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button 
          onClick={handleClose} 
          disabled={loading}
          color="inherit"
        >
          {response ? "Close" : "Cancel"}
        </Button>
        {!response && (
          <Button
            onClick={handleImport}
            disabled={!file || loading}
            variant="contained"
            color="primary"
            size="large"
          >
            {loading ? "Importing..." : "Import Records"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AttendanceImportDialog;
